/**
 * AllCategoriesPage — `/categories` — Lists every active business category
 * with filter chips at the top and a grouped subcategory layout below.
 * Matches the second screenshot the user shared.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { api } from "../../lib/api";
import BizCategoryIcon from "../../components/BizCategoryIcon";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import BkCategoriesView from "./blinkit/BkCategoriesView";

export default function AllCategoriesPage() {
    return useIsBlinkit() ? <BkCategoriesView /> : <ClassicAllCategoriesPage />;
}

function ClassicAllCategoriesPage() {
    const [cats, setCats] = useState([]);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/business-categories").then(({ data }) => setCats(data || []));
    }, []);

    const parents = useMemo(() => cats.filter((c) => !c.parent_id), [cats]);
    const byParent = useMemo(() => {
        const m = {};
        for (const c of cats) {
            if (c.parent_id) (m[c.parent_id] = m[c.parent_id] || []).push(c);
        }
        return m;
    }, [cats]);

    const q = query.trim().toLowerCase();
    const showParents = active ? parents.filter((p) => p.id === active) : parents;
    const matches = (name) => !q || name.toLowerCase().includes(q);

    return (
        <div className="pb-32" data-testid="all-categories-page">
            <header className="sticky top-0 bg-background z-10 px-4 py-3 border-b border-border flex items-center gap-3">
                <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:bg-muted" data-testid="all-cats-back"><ArrowLeft size={20} /></button>
                <h1 className="text-lg font-bold">Categories</h1>
            </header>

            <div className="px-4 pt-3">
                <div className="flex items-center gap-2 bg-muted rounded-xl px-3 py-2.5">
                    <Search size={18} className="text-muted-foreground" />
                    <input
                        data-testid="cats-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search"
                        className="bg-transparent outline-none flex-1 text-sm"
                    />
                </div>
            </div>

            {/* Top filter chips — one per parent category */}
            <div className="flex gap-2 overflow-x-auto px-4 py-3 no-scrollbar" data-testid="cats-chips">
                <button
                    onClick={() => setActive(null)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border whitespace-nowrap inline-flex items-center gap-1.5 ${!active ? "bg-foreground text-background border-foreground" : "border-border"}`}
                >All</button>
                {parents.map((p) => (
                    <button
                        key={p.id}
                        data-testid={`cats-chip-${p.slug}`}
                        onClick={() => setActive((cur) => (cur === p.id ? null : p.id))}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border whitespace-nowrap inline-flex items-center gap-1.5 ${active === p.id ? "bg-foreground text-background border-foreground" : "border-border text-foreground"}`}
                    >
                        <BizCategoryIcon name={p.icon} size={14} className="text-amber-500" />
                        {p.name}
                    </button>
                ))}
            </div>

            {/* Grouped categories */}
            <div className="px-4 mt-2 space-y-7 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-x-8 lg:gap-y-7 lg:items-start xl:grid-cols-4 2xl:grid-cols-5">
                {showParents.map((p) => {
                    const subs = (byParent[p.id] || []).filter((s) => matches(s.name));
                    const showParentHeader = matches(p.name) || subs.length > 0;
                    if (!showParentHeader) return null;
                    return (
                        <section key={p.id} data-testid={`cats-section-${p.slug}`}>
                            <div className="flex items-center gap-2 mb-3">
                                <BizCategoryIcon name={p.icon} size={22} className="text-amber-500" strokeWidth={2.4} />
                                <h2 className="text-lg font-extrabold">{p.name}</h2>
                            </div>
                            {subs.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {subs.map((s) => (
                                        <button
                                            key={s.id}
                                            data-testid={`cats-sub-${s.slug}`}
                                            onClick={() => navigate(`/c/${s.slug}`)}
                                            className="text-sm font-medium px-4 py-2 rounded-full border border-border hover:border-foreground transition"
                                        >{s.name}</button>
                                    ))}
                                </div>
                            ) : (
                                <button
                                    onClick={() => navigate(p.slug === "food" ? "/food" : `/c/${p.slug}`)}
                                    className="text-xs font-semibold text-primary"
                                >Browse {p.name} →</button>
                            )}
                        </section>
                    );
                })}
            </div>
        </div>
    );
}
